import {
  answerResponseSchema,
  chunkListSchema,
  gradeResponseSchema,
  guideSchema,
  mockAnswerResponseSchema,
  mindMapSchema,
  mockResultsSchema,
  mockSchema,
  programmeConfigResponseSchema,
  reviewCardListSchema,
  sessionSchema,
  sessionSummarySchema,
  todayPlanSchema,
  todaySessionResponseSchema,
  type AnswerRequest,
  type CreateFlagRequest,
  type CreateMockRequest,
  type CreateSessionRequest,
  type FeatureKey,
  type GradeRequest,
  type MindMapTree,
  type ItemResponse,
} from "@studyward/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "./api";
import { keys, usePricing, useUsage } from "./queries";
import { useSession } from "./session";

const okSchema = z.object({ ok: z.literal(true) });
const useSignedIn = () => useSession().status === "signed_in";

export const studyKeys = {
  due: (setId: string | null) => ["due", setId ?? "all"] as const,
  session: (id: string) => ["session", id] as const,
  summary: (id: string) => ["session-summary", id] as const,
  chunks: (ids: string[]) => ["chunks", [...ids].sort().join(",")] as const,
  mock: (id: string) => ["mock", id] as const,
  results: (id: string) => ["mock-results", id] as const,
  today: ["today"] as const,
  config: (programme: string | null | undefined) => ["programme-config", programme] as const,
};

/**
 * Whether the signed-in plan includes a feature (UX only — the API enforces it). `known` is false while the plan or
 * the offer is still loading; callers that would spend a request on a Pro-only endpoint wait for it.
 */
export function useFeatureAccess(feature: FeatureKey): { known: boolean; allowed: boolean } {
  const pricing = usePricing();
  const usage = useUsage();
  if (!pricing.data || !usage.data) return { known: false, allowed: true };
  return { known: true, allowed: usage.data.plan === "pro" || pricing.data.free_features.includes(feature) };
}

/** Unknown while loading counts as allowed so nothing flickers to "Pro". */
export const useHasFeature = (feature: FeatureKey): boolean => useFeatureAccess(feature).allowed;

// ---------------------------------------------------------------- flashcards
export function useDueCards(setId: string | null) {
  return useQuery({
    queryKey: studyKeys.due(setId),
    queryFn: () => api.get(`/review/due${setId ? `?set_id=${setId}` : ""}`, reviewCardListSchema),
    enabled: useSignedIn(),
    // The queue is read once per run; grading must not reshuffle the cards under the student.
    staleTime: Infinity,
    gcTime: 0,
  });
}

/** Grading is idempotent on `client_review_id`, so the caller can safely retry the same body after an error. */
export function useGrade() {
  return useMutation({ mutationFn: ({ cardId, body }: { cardId: string; body: GradeRequest }) => api.post(`/review/${cardId}/grade`, gradeResponseSchema, body) });
}

export function useLogFlashcardSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { set_id: string | null; reviewed: number; duration_seconds: number; client_session_id: string }) =>
      api.post("/sessions/flashcards", sessionSummarySchema, body),
    onSuccess: (summary) => {
      queryClient.setQueryData(studyKeys.summary(summary.id), summary);
      void queryClient.invalidateQueries({ queryKey: keys.progress });
      void queryClient.invalidateQueries({ queryKey: studyKeys.today });
    },
  });
}

// ---------------------------------------------------------------- practice sessions
export function useCreateSession() {
  return useMutation({ mutationFn: (body: CreateSessionRequest) => api.post("/sessions", sessionSchema, body) });
}

export function useStudySession(id: string) {
  return useQuery({ queryKey: studyKeys.session(id), queryFn: () => api.get(`/sessions/${id}`, sessionSchema), enabled: useSignedIn() && !!id, staleTime: Infinity });
}

export function useAnswer(sessionId: string) {
  return useMutation({ mutationFn: (body: AnswerRequest) => api.post(`/sessions/${sessionId}/answer`, answerResponseSchema, body) });
}

export function useCompleteSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/sessions/${id}/complete`, sessionSummarySchema),
    onSuccess: (summary) => {
      queryClient.setQueryData(studyKeys.summary(summary.id), summary);
      void queryClient.invalidateQueries({ queryKey: keys.progress });
      void queryClient.invalidateQueries({ queryKey: studyKeys.today });
    },
  });
}

export function useSessionSummary(id: string) {
  return useQuery({ queryKey: studyKeys.summary(id), queryFn: () => api.get(`/sessions/${id}/summary`, sessionSummarySchema), enabled: useSignedIn() && !!id });
}

/** Citation text for "Source: Slide 4". */
export function useChunks(ids: string[]) {
  return useQuery({
    queryKey: studyKeys.chunks(ids),
    queryFn: async () => (await api.get(`/chunks?ids=${ids.join(",")}`, chunkListSchema)).items,
    enabled: useSignedIn() && ids.length > 0,
    staleTime: Infinity,
  });
}

export function useFlag() {
  return useMutation({ mutationFn: (body: CreateFlagRequest) => api.post("/flags", okSchema, body) });
}

// ---------------------------------------------------------------- today's session
export function useTodayPlan() {
  return useQuery({ queryKey: studyKeys.today, queryFn: () => api.get("/today", todayPlanSchema), enabled: useSignedIn() });
}

export function useStartToday() {
  return useMutation({ mutationFn: () => api.post("/sessions/today", todaySessionResponseSchema, {}) });
}

// ---------------------------------------------------------------- mock exams
/** The exam format in force: sections, pace and delivery, so setup shows what the real exam looks like. */
export function useProgrammeConfig(programme: string | null | undefined) {
  return useQuery({
    queryKey: studyKeys.config(programme),
    queryFn: () => api.get(`/programmes/${programme}/config`, programmeConfigResponseSchema),
    enabled: useSignedIn() && !!programme,
    staleTime: 5 * 60_000,
  });
}

export function useCreateMock() {
  return useMutation({ mutationFn: (body: CreateMockRequest) => api.post("/mock-exams", mockSchema, body) });
}

export function useMock(id: string) {
  return useQuery({ queryKey: studyKeys.mock(id), queryFn: () => api.get(`/mock-exams/${id}`, mockSchema), enabled: useSignedIn() && !!id, staleTime: Infinity });
}

export function useMockAnswer(id: string) {
  return useMutation({
    mutationFn: (body: { question_id: string; response: ItemResponse | null; seconds: number; flagged: boolean }) =>
      api.post(`/mock-exams/${id}/answer`, mockAnswerResponseSchema, body),
  });
}

export function useSubmitMock(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`/mock-exams/${id}/submit`, okSchema, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["progress"] });
      void queryClient.invalidateQueries({ queryKey: keys.usage });
    },
  });
}

export function useMockResults(id: string) {
  return useQuery({ queryKey: studyKeys.results(id), queryFn: () => api.get(`/mock-exams/${id}/results`, mockResultsSchema), enabled: useSignedIn() && !!id });
}

// ---------------------------------------------------------------- study guide and mind map
export function useGuide(setId: string) {
  return useQuery({ queryKey: ["guide", setId] as const, queryFn: () => api.get(`/sets/${setId}/guide`, guideSchema), enabled: useSignedIn() && !!setId });
}

export function useMindMap(setId: string) {
  return useQuery({ queryKey: ["mind-map", setId] as const, queryFn: () => api.get(`/sets/${setId}/mind-map`, mindMapSchema), enabled: useSignedIn() && !!setId, retry: false });
}

export function useSaveMindMap(setId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tree: MindMapTree) => api.patch(`/sets/${setId}/mind-map`, mindMapSchema, { tree }),
    onSuccess: (data) => queryClient.setQueryData(["mind-map", setId], data),
  });
}
